import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { ProtocolGraph } from "@/components/ProtocolGraph";
import { DataPanel, MonoLabel } from "@/components/primitives";
import { PROTOCOL_NODES } from "@/lib/demo-data";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

const STACK = ["INTENT", "MEASUREMENT", "EVIDENCE", "VERIFICATION", "SETTLEMENT"];

export default function Protocol() {
  useSEO({
    title: "Protocol — Consequence Protocol",
    description:
      "The Consequence Layer: a seven-layer architecture that transforms observable actions into verifiable outcomes and compounding reputation.",
    path: "/protocol",
  });

  return (
    <PageShell testId="page-protocol">
      <section className="container-t pb-16 pt-20 sm:pt-28">
        <SectionHeader
          index="00"
          label="Protocol"
          title={
            <>
              The <span className="text-cyan glow-cyan">Consequence</span> Layer
            </>
          }
          description="Consequence Protocol is infrastructure for measuring what actions actually accomplish. Seven layers take an actor from raw action to a portable, verified track record."
        />
      </section>

      <section className="container-t pb-24" data-testid="section-architecture">
        <Reveal>
          <ProtocolGraph />
        </Reveal>
      </section>

      <section className="border-t border-line bg-panel/40 py-24" data-testid="section-layers">
        <div className="container-t">
          <SectionHeader index="01" label="Architecture" title="Seven layers, one pipeline" />
          <div className="mt-12">
            {PROTOCOL_NODES.map((n, i) => (
              <Reveal key={n.id} delay={i * 0.04}>
                <div
                  className="group grid grid-cols-[auto_1fr] items-baseline gap-5 border-b border-line/70 py-6 transition-colors duration-200 hover:bg-cyan/5 sm:grid-cols-[80px_220px_1fr] sm:gap-8"
                  data-testid={`protocol-layer-${n.name.toLowerCase()}`}
                >
                  <span className="font-mono text-xs text-cyan">{n.id}</span>
                  <span className="font-display text-lg font-bold uppercase tracking-wide text-ink transition-colors duration-200 group-hover:text-cyan sm:text-xl">
                    {n.name}
                  </span>
                  <span className="col-span-2 pl-0 text-sm leading-relaxed text-dim sm:col-span-1 sm:pl-0">{n.desc} {n.detail}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-t py-24" data-testid="section-stack">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <DataPanel title="Verification Stack" meta="CONCEPT">
              <div className="space-y-2">
                {STACK.map((s, i) => (
                  <div
                    key={s}
                    className="flex items-center justify-between border border-line/70 bg-base/60 px-4 py-3 transition-colors duration-200 hover:border-electric/40"
                    style={{ marginLeft: `${i * 18}px` }}
                  >
                    <MonoLabel className="text-[10px] text-ink/90">LAYER {i + 1} · {s}</MonoLabel>
                    <span className="font-mono text-[10px] text-cyan">0{i + 1}</span>
                  </div>
                ))}
              </div>
            </DataPanel>
          </Reveal>
          <Reveal delay={0.1}>
            <DataPanel title="Design Principles" meta="NOTES">
              <ul className="space-y-4 text-sm leading-relaxed text-dim">
                <li className="flex gap-3"><span className="text-cyan">▸</span>Claims must be falsifiable before they are accepted — vague intent cannot be verified.</li>
                <li className="flex gap-3"><span className="text-cyan">▸</span>Evidence sources are declared at contract creation, so proof standards are agreed in advance.</li>
                <li className="flex gap-3"><span className="text-cyan">▸</span>Verification is deterministic wherever the rules permit; disputes resolve the rest.</li>
                <li className="flex gap-3"><span className="text-cyan">▸</span>Settlement is append-only. Consequences — good and bad — are permanent.</li>
                <li className="flex gap-3"><span className="text-cyan">▸</span>Reputation is a read-only projection of settled history. It cannot be bought, only earned.</li>
              </ul>
            </DataPanel>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
